import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { getStatusCodeFromError } from "@entrilia/shared/api/axiosHelper";
import type { TokenOperationResponse } from "@entrilia/shared/api/clients/ssoTypes";
import { ssoApi } from "@entrilia/shared/api/sso";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { RedirectKeys } from "@entrilia/shared/auth/authTypes";
import { getAuthSettings } from "@entrilia/shared/auth/settings";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import { logError, trackEvent } from "@entrilia/shared/logging";
import { getRegistrationFailureEvent } from "./registrationFailureTelemetry";
import { resolveClientCode } from "./accessCheck/accessCheckClientCode";
import { handleRedirectUrlState } from "@entrilia/shared/utilities/handleRedirectUrlState";
import { parseJsonObject } from "@entrilia/shared/utilities/objectHelper";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { getSharedVariables } from "@entrilia/shared/variables";
import { UserActions } from "../store/state/user/actions";
import AccessCheckPage from "./AccessCheckPage";

type AuthenticationState = "auth_started" | "authenticated" | "auth_failed" | "forbidden";

interface StateParams {
  flow?: string; // B2C login customization page sets 'flow' when "Forgot password?" is used
  userFlow?: string;
}

const Authentication = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [authenticationState, setAuthenticationState] = useState<AuthenticationState>("auth_started");

  const authCode = searchParams.get(RedirectKeys.Code);
  const stateParam = searchParams.get(RedirectKeys.State);
  const errorParam = searchParams.get(RedirectKeys.Error);

  const acquireToken = useCallback(
    async (userFlow: string): Promise<AuthenticationState | undefined> => {
      if (!authCode) {
        return undefined;
      }

      try {
        const { clientId, cookieTarget } = getAuthSettings();
        const redirectUri = getSharedVariables().PORTAL_REDIRECT_URI;

        await ssoApi.requestToken({
          clientId,
          redirectUri,
          authCode,
          userFlow,
          cookieTarget,
        });

        return "authenticated";
      } catch (error) {
        const status = getStatusCodeFromError(error);
        if (status === 403) {
          // Backend returns 403 with the user's email in the response body when B2C
          // authentication succeeds but the user has no Entrilia DB record. Stash it
          // so the access-check troubleshooter can skip the email-input step.
          if (axios.isAxiosError<ApiResponse<TokenOperationResponse>>(error)) {
            const email = error.response?.data?.data?.email;
            if (email) {
              dispatch(UserActions.UpdateState({ email }));
            }
          }
          return "forbidden";
        }

        const event = getRegistrationFailureEvent({
          isSignUpFlow: userFlow === getAuthSettings().userFlowSignUp,
          outcome: "auth_failed",
          status,
          clientCode: resolveClientCode(),
        });
        if (event) {
          trackEvent(event.name, event.properties);
        }
        return "auth_failed";
      }
    },
    [authCode, dispatch]
  );

  useEffect(() => {
    if (errorParam) {
      const errorState = stateParam ? parseJsonObject<StateParams>(stateParam) : null;
      const { userFlowSignUp, userFlowPasswordReset, userFlowSignIn } = getAuthSettings();
      const resolvedFlow =
        errorState?.flow === "password_reset" ? userFlowPasswordReset : errorState?.userFlow || userFlowSignIn;
      const event = getRegistrationFailureEvent({
        isSignUpFlow: resolvedFlow === userFlowSignUp,
        outcome: "b2c_error",
        errorCode: errorParam,
        errorDescription: searchParams.get("error_description") ?? undefined,
        clientCode: resolveClientCode(),
      });
      if (event) {
        trackEvent(event.name, event.properties);
      }
      // Let the JSX guard below render the AccessCheckPage
      return undefined;
    }

    if (!stateParam) {
      navigate("/");
      return undefined;
    }

    const state = parseJsonObject<StateParams>(stateParam);
    if (!state) {
      navigate("/");
      return undefined;
    }

    if (authenticationState !== "auth_started") {
      return undefined;
    }

    const { userFlowPasswordReset, userFlowSignIn } = getAuthSettings();
    const userFlow = state.flow === "password_reset" ? userFlowPasswordReset : state.userFlow || userFlowSignIn;

    let isActive = true;
    acquireToken(stringifyValue(userFlow)).then((nextAuthenticationState) => {
      if (isActive && nextAuthenticationState) {
        setAuthenticationState(nextAuthenticationState);
      }
    });
    return () => {
      isActive = false;
    };
  }, [authenticationState, stateParam, acquireToken, navigate, errorParam, searchParams]);

  if (errorParam) {
    return <AccessCheckPage />;
  }

  if (!authCode) {
    return <Navigate to="/" />;
  }

  switch (authenticationState) {
    case "auth_started": {
      return <FullScreenLoader title="Almost there!" subtitle="We're authorizing your access..." />;
    }
    case "auth_failed": {
      return <AccessCheckPage />;
    }
    case "forbidden": {
      return <AccessCheckPage />;
    }
    case "authenticated": {
      const redirected = handleRedirectUrlState();
      if (!redirected) {
        navigate("/");
      }
      return null;
    }
    default: {
      logError(`Unexpected AuthenticationState value: ${authenticationState}`, "[Authentication]");
      return null;
    }
  }
};

export default Authentication;
